import React, { useCallback, useEffect, useRef, useState } from "react";
import LudDashboardModal from "../../modals/LudDashboardModal";
import { Button, NoticeBox, Tags } from "../../ui";
import BranchPicker from "./BranchPicker";
import { createSubscriptionAPI, getApplicationDetailAPI, previewSubscriptionAPI } from "../../../utils/api/applications";
import { errorMessage } from "./applicationsText";
import { newIdempotencyKey } from "./applicationsModel";

// Subscribe flow: (1) the server confirms the store is 100% complete,
// (2) the user picks eligible branches, (3) the registration email is shown in
// full and only sent when the user confirms (or saved as pending when they
// explicitly choose to). Closing the window never creates a request.
// A branch can be registered when it is complete, has no request yet and no integration.
const isSelectable = (branch) => branch.eligibility.eligible && !branch.subscription && branch.integration === "unconfigured";

export default function SubscribeModal({ open, storeId, app, appName, lang, t, token, initialBranchIds = [], onClose, onDone, onGoCompletion }) {
  const [step, setStep] = useState("check"); // check | incomplete | branches | preview
  const [detail, setDetail] = useState(null);
  const [missing, setMissing] = useState([]);
  const [percent, setPercent] = useState(0);
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const idempotencyKey = useRef(newIdempotencyKey());
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const initialKey = initialBranchIds.join(",");
  const load = useCallback(async () => {
    setStep("check");
    setError("");
    setSelected([]);
    setPreview(null);
    idempotencyKey.current = newIdempotencyKey();
    try {
      const res = await getApplicationDetailAPI(token, storeId, app.key);
      if (!alive.current) return;
      setDetail(res.data);
      // The branch the flow was opened from starts selected (if it can be registered).
      setSelected(res.data.integration.branches.filter((branch) => initialKey.split(",").includes(branch.id) && isSelectable(branch)).map((branch) => branch.id));
      setPercent(res.data.completion.percent);
      setMissing(res.data.completion.missing);
      setStep(res.data.completion.complete ? "branches" : "incomplete");
    } catch (e) {
      if (!alive.current) return;
      setError(errorMessage(t, e));
      setStep("branches");
      setDetail({ integration: { branches: [] } });
    }
  }, [token, storeId, app?.key, t, initialKey]);

  useEffect(() => {
    if (open && app) load();
  }, [open, app, load]);

  if (!open || !app) return null;

  const branches = detail?.integration?.branches || [];
  const blockedReason = (branch) => {
    if (!branch.eligibility.eligible) return branch.eligibility.missing.map((field) => t.missingFields[field] || field).join(" · ");
    if (branch.subscription) return `${t.subscribe.alreadyRequested} (${t.status[branch.subscription.status] || branch.subscription.status})`;
    if (branch.integration !== "unconfigured") return t.status[branch.integration] || branch.integration;
    return "";
  };
  const selectable = (branch) => !blockedReason(branch);

  const toggle = (id) => {
    // A different selection is a different request: it gets its own key.
    idempotencyKey.current = newIdempotencyKey();
    setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  const goPreview = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await previewSubscriptionAPI(token, storeId, app.key, { branchIds: selected, lang });
      if (!alive.current) return;
      setPreview(res.data);
      setStep("preview");
    } catch (e) {
      if (!alive.current) return;
      if (e.code === "STORE_INCOMPLETE") {
        setMissing(e.data?.completion?.missing || []);
        setPercent(e.data?.completion?.percent ?? 0);
        setStep("incomplete");
      } else setError(errorMessage(t, e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const submit = async (send) => {
    setBusy(true);
    setError("");
    try {
      await createSubscriptionAPI(token, storeId, app.key, { branchIds: selected, lang, idempotencyKey: idempotencyKey.current, send });
      onDone?.(send ? t.subscribe.sentOk : t.subscribe.savedOk, "success");
    } catch (e) {
      // A failed send still left a saved request behind: tell the user where it is.
      if (e.code === "SEND_FAILED" || e.data?.request) onDone?.(t.subscribe.failedSaved, "error");
      else if (alive.current) setError(errorMessage(t, e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const stepIndex = { check: 0, incomplete: 0, branches: 1, preview: 2 }[step];

  const footer =
    step === "branches" ? (
      <>
        <Button variant="primary" appearance="filled" onClick={goPreview} disabled={busy || !selected.length} loading={busy}>
          {t.next}
        </Button>
      </>
    ) : step === "preview" && (
      <>
        <Button variant="neutral" appearance="soft" onClick={() => setStep("branches")} disabled={busy}>
          {t.back}
        </Button>
        <Button variant="primary" appearance="soft" onClick={() => submit(false)} disabled={busy}>
          {t.subscribe.savePending}
        </Button>
        <Button variant="success" appearance="filled" onClick={() => submit(true)} disabled={busy} loading={busy}>
          {t.subscribe.sendNow}
        </Button>
      </>
    );

  return (
    <LudDashboardModal open title={t.subscribe.title(appName)} size="lg" onClose={onClose} busy={busy} footer={footer}>
      <ol className="lud-app-steps" aria-label="steps">
        {t.subscribe.steps.map((label, index) => (
          <li key={label} className={index === stepIndex ? "is-current" : index < stepIndex ? "is-done" : ""} aria-current={index === stepIndex ? "step" : undefined}>
            <span>{index + 1}</span>
            {label}
          </li>
        ))}
      </ol>

      {error && (
        <NoticeBox variant="danger" size="sm">
          {error}
        </NoticeBox>
      )}

      {step === "check" && <p className="lud-app-lead" role="status">{t.subscribe.checking}</p>}

      {step === "incomplete" && (
        <div className="lud-app-stack">
          <NoticeBox variant="warning" title={t.subscribe.incompleteTitle(percent)}>
            {t.subscribe.incompleteBody}
          </NoticeBox>
          <strong>{t.subscribe.missing}</strong>
          <ul className="lud-app-missing">
            {missing.map((item) => (
              <li key={item.id}>
                <span>{lang === "ar" ? item.ar : item.en}</span>
                <small>{lang === "ar" ? item.sectionAr : item.sectionEn}</small>
              </li>
            ))}
          </ul>
          <Button variant="primary" appearance="filled" onClick={onGoCompletion}>
            {t.subscribe.goCompletion}
          </Button>
        </div>
      )}

      {step === "branches" && (
        <div className="lud-app-stack">
          <p className="lud-app-lead">{t.subscribe.pickBranches}</p>
          {!branches.some(selectable) && <NoticeBox variant="warning" size="sm">{t.subscribe.noEligible}</NoticeBox>}
          <BranchPicker branches={branches} selected={selected} onToggle={toggle} reasonFor={blockedReason} originId={initialBranchIds[0] || null} t={t} disabled={busy} />
          <small>{t.subscribe.selectedCount(selected.length)}</small>
        </div>
      )}

      {step === "preview" && preview && (
        <div className="lud-app-stack">
          <p className="lud-app-hint">{t.subscribe.previewHint}</p>
          <dl className="lud-app-kv">
            <dt>{t.subscribe.to}</dt>
            <dd dir="ltr">{preview.email.to.join(", ")}</dd>
            <dt>{t.subscribe.cc}</dt>
            <dd dir="ltr">{preview.email.cc.join(", ") || "—"}</dd>
            <dt>{t.subscribe.subject}</dt>
            <dd>{preview.email.subject}</dd>
            <dt>{t.subscribe.branches}</dt>
            <dd>
              {preview.branches.map((branch) => (
                <Tags key={branch.id} variant="primary" appearance="soft" size="sm">
                  {branch.name}
                </Tags>
              ))}
            </dd>
          </dl>
          <strong>{t.subscribe.body}</strong>
          {/* The e-mail is rendered sandboxed: no script, form or navigation can run from it. */}
          <iframe className="lud-app-mail-frame" title={t.subscribe.previewTitle} sandbox="" srcDoc={preview.email.html} />
        </div>
      )}
    </LudDashboardModal>
  );
}
